import {graphql} from 'react-relay';
import {fetchQuery} from 'relay-runtime';
import environment from '../../../../RelayEnvironment';
import type {novaRunningPollerQuery} from './__generated__/novaRunningPollerQuery.graphql';

// While any task on screen is Working on a run this tab did not start - another tab, before a
// reload, a teammate - its Nova fields are re-read every ten seconds, so the control turns into the
// result shortly after the run finishes rather than at the next page load. The read writes into the
// same Task records every row and card already reads through, so each of them re-renders on its own.
// It stops once nothing on screen is running. A run this tab started needs none of this: its own
// mutation returns the result.
export const NOVA_RUNNING_POLL_INTERVAL_MS = 10000;

const query = graphql`
	query novaRunningPollerQuery($ids: [ID!]!) {
		nodes(ids: $ids) {
			... on Task {
				id
				novaRunningRunId
				hasNovaConversation
			}
		}
	}
`;

// How many mounted controls are watching each task: one task can show in a row and in the modal at
// once, and the poll must outlive the first of them to unmount.
const watchers = new Map<string, number>();
let timer: ReturnType<typeof setInterval> | null = null;

function poll(): void {
	const ids = [...watchers.keys()];
	if (ids.length === 0) return;
	fetchQuery<novaRunningPollerQuery>(environment.getInstance(), query, {ids}, {fetchPolicy: 'network-only'})
		.toPromise()
		.catch(() => {
			// A missed poll is retried on the next tick; the control just stays Working a little longer.
		});
}

function stopIfIdle(): void {
	if (watchers.size > 0 || timer === null) return;
	clearInterval(timer);
	timer = null;
}

// Starts watching a task, and returns the call that stops watching it.
export function watchRunningTask(taskId: string): () => void {
	watchers.set(taskId, (watchers.get(taskId) ?? 0) + 1);
	if (timer === null) timer = setInterval(poll, NOVA_RUNNING_POLL_INTERVAL_MS);
	let released = false;
	return () => {
		if (released) return;
		released = true;
		const remaining = (watchers.get(taskId) ?? 1) - 1;
		if (remaining > 0) watchers.set(taskId, remaining);
		else watchers.delete(taskId);
		stopIfIdle();
	};
}

// For tests: which tasks are being watched right now.
export function watchedRunningTasks(): string[] {
	return [...watchers.keys()];
}
